// Only inline training forms opt in. Requests own a different viewport lifecycle.
export function watchTrainingFeedbackViewport(root, {
  viewport = window.visualViewport,
  documentObject = document,
  windowObject = window,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  if (!root || !viewport) return () => {};
  const geometry = () => ({ height: viewport.height, width: viewport.width, scale: viewport.scale || 1 });
  let normal = geometry();
  let session = null;
  let timer = null;
  const clearPending = () => { if (timer !== null) clearTimer(timer); timer = null; };
  const cancel = () => { clearPending(); session = null; };
  const isField = (target) => target?.matches?.('textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="range"])');
  const isTrainingField = (target) => isField(target) && root.contains(target)
    && Boolean(target.closest('[data-training-keyboard-restore]'));
  const capture = (event) => {
    if (!isTrainingField(event.target)) return;
    // Preserve the pre-keyboard origin when moving between fields in this form.
    if (session) return;
    const current = geometry();
    if (normal.height - current.height > 120 || Math.abs(current.scale - 1) > .01) return;
    normal = current;
    const positions = [];
    for (let element = event.target.parentElement; element; element = element.parentElement) {
      const overflow = windowObject.getComputedStyle(element).overflowY;
      if (/(auto|scroll|overlay)/.test(overflow) && element.scrollHeight > element.clientHeight) {
        positions.push({ element, top: element.scrollTop, left: element.scrollLeft });
      }
    }
    const page = documentObject.scrollingElement;
    if (page && !positions.some(({ element }) => element === page)) {
      positions.push({ element: page, top: page.scrollTop, left: page.scrollLeft });
    }
    session = { baseline: current, positions, form: event.target.closest('[data-training-keyboard-restore]'), opened: false };
  };
  const focus = (event) => {
    if (isTrainingField(event.target)) capture(event);
    else if (isField(event.target)) cancel(); // Never restore over another form/chat.
  };
  const blur = () => { if (session && !session.opened) cancel(); };
  const update = () => {
    clearPending();
    const current = geometry();
    if (!session) { normal = current; return; }
    const { baseline } = session;
    // Rotation and pinch zoom are user navigation, not keyboard dismissal.
    if (Math.abs(current.width - baseline.width) > 2 || Math.abs(current.scale - baseline.scale) > .01) {
      cancel(); normal = current; return;
    }
    if (baseline.height - current.height > 120) session.opened = true;
    if (!session.opened || current.height < baseline.height - 2) return;
    // Safari may keep panning after its final resize. Restart on viewport AND
    // ancestor scroll events; restore once, only after 180 ms of stable geometry.
    timer = setTimer(() => {
      timer = null;
      const saved = session;
      if (!saved || !root.contains(saved.form)) { cancel(); return; }
      const settled = geometry();
      if (settled.height < saved.baseline.height - 2) return;
      session = null;
      normal = settled;
      saved.positions.forEach(({ element, top, left }) => {
        if (!element.isConnected) return;
        const behavior = element.style.scrollBehavior;
        element.style.scrollBehavior = 'auto';
        element.scrollTop = top;
        element.scrollLeft = left;
        element.style.scrollBehavior = behavior;
      });
    }, 180);
  };
  const pageScroll = () => { if (session?.opened) update(); };
  root.addEventListener('pointerdown', capture, true);
  documentObject.addEventListener('focusin', focus, true);
  root.addEventListener('focusout', blur, true);
  documentObject.addEventListener('scroll', pageScroll, true);
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
  windowObject.addEventListener('orientationchange', cancel);
  return () => {
    cancel();
    root.removeEventListener('pointerdown', capture, true);
    documentObject.removeEventListener('focusin', focus, true);
    root.removeEventListener('focusout', blur, true);
    documentObject.removeEventListener('scroll', pageScroll, true);
    viewport.removeEventListener('resize', update);
    viewport.removeEventListener('scroll', update);
    windowObject.removeEventListener('orientationchange', cancel);
  };
}
